// src/api/deepkiApi.ts — client du connecteur deepki (ts-ingest, process indépendant de report-api,
// cf. docs/ts-ingest/ts-ingest.md). Appelle directement /ts/deepki/... (jamais via /api).

import { APP_CONFIG } from "../config/app.config";

const BASE = APP_CONFIG.tsIngestBaseUrl;

async function tsRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: init?.body != null ? { "Content-Type": "application/json" } : undefined,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => res.statusText);
    throw new Error(`ts-ingest ${res.status}: ${body}`);
  }
  return res.json() as Promise<T>;
}

export interface DeepkiValidationReport {
  pas:         "jour" | "heure";
  rowCount:    number;
  tsFrom:      number; // epoch secondes
  tsTo:        number; // epoch secondes
  gapsCount:   number;
  gapsPreview: string[]; // ISO, plafonné
  warnings:    string[];
}

export interface DeepkiIngestResult {
  ok:            boolean;
  rowsIngested:  number;
  storedFilename: string;
  report:        DeepkiImportRecord;
}

export interface DeepkiImportRecord {
  id:               string;
  siteId:           string;
  pdlId:            string;
  originalFilename: string;
  storedFilename:   string;
  pas:              string;
  rowCount:         number;
  tsFrom:           number;
  tsTo:             number;
  gapsCount:        number;
  createdAt:        string;
  deletedAt:        string | null;
}

export function validateDeepkiFile(input: { pdlId: string; filename: string; dataBase64: string }): Promise<DeepkiValidationReport> {
  return tsRequest<DeepkiValidationReport>("/deepki/validate", { method: "POST", body: JSON.stringify(input) });
}

export function ingestDeepkiFile(input: { siteId: string; pdlId: string; filename: string; dataBase64: string }): Promise<DeepkiIngestResult> {
  return tsRequest<DeepkiIngestResult>("/deepki/ingest", { method: "POST", body: JSON.stringify(input) });
}

export function getDeepkiReadings(
  pdlId: string, fromMs?: number, toMs?: number, pas?: "jour" | "heure",
): Promise<{ data: [number, number][] }> {
  const qs = new URLSearchParams();
  if (fromMs != null) qs.set("from", String(fromMs));
  if (toMs   != null) qs.set("to",   String(toMs));
  if (pas    != null) qs.set("pas",  pas);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return tsRequest<{ data: [number, number][] }>(`/deepki/readings/${encodeURIComponent(pdlId)}${suffix}`);
}

export function getDeepkiLastImport(pdlId: string): Promise<DeepkiImportRecord | null> {
  return tsRequest<DeepkiImportRecord | null>(`/deepki/imports/${encodeURIComponent(pdlId)}`);
}

/** Nombre de relevés actuellement en base pour ce PDL, par pas — les deux tables peuvent être non
 * nulles simultanément (cf. docs/ts-ingest/deepki-connector.md) ; ne pas confondre avec
 * `DeepkiImportRecord.rowCount`, qui ne reflète que le dernier import. */
export function getDeepkiCounts(pdlId: string): Promise<{ jour: number; heure: number }> {
  return tsRequest<{ jour: number; heure: number }>(`/deepki/counts/${encodeURIComponent(pdlId)}`);
}

/** Supprime les relevés deepki d'un PDL ; marque son historique d'import comme purgé (jamais supprimé) — réservé aux admins côté UI (cf. DeepkiImportPanel). */
export function purgeDeepkiData(pdlId: string): Promise<{ ok: boolean; readingsDeleted: number; importsMarkedDeleted: number }> {
  return tsRequest(`/deepki/pdl/${encodeURIComponent(pdlId)}`, { method: "DELETE" });
}
